// src/pages/OrgDashboard.jsx
import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert,
  Grid, Chip, Table, TableHead, TableRow,
  TableCell, TableBody, LinearProgress,
} from '@mui/material';
import BusinessIcon          from '@mui/icons-material/Business';
import PeopleIcon            from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GradeIcon             from '@mui/icons-material/Grade';
import { useTranslation }    from 'react-i18next';
import api                   from '../api/axios';
import { useOrg }            from '../context/OrgContext';
import { useAuth }           from '../auth/useAuth';

function StatCard({ label, value, color = '#0f766e', sub }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
      <Typography fontSize={12} color="text.secondary" mb={0.5}>{label}</Typography>
      <Typography fontSize={26} fontWeight={700} color={color}>{value}</Typography>
      {sub && <Typography fontSize={11} color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

export default function OrgDashboard() {
  const { t }               = useTranslation('org');
  const { user }            = useAuth();
  const { org, orgId }      = useOrg();
  const [stats,   setStats]   = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    Promise.all([
      api.get('/orgs/me/stats').catch(() => ({ data: {} })),
      api.get('/orgs/me/members?limit=20').catch(() => ({ data: [] })),
    ]).then(([statsR, membersR]) => {
      setStats(statsR.data);
      setMembers(Array.isArray(membersR.data) ? membersR.data : membersR.data?.members || []);
    }).catch(e => setError(e?.response?.data?.detail?.message || t('error', { ns: 'common', defaultValue: 'Erreur' })))
      .finally(() => setLoading(false));
  }, [orgId, t]);

  if (!user?.isOrgAdmin) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Alert severity="warning">{t('notAuthorized', { ns: 'common', defaultValue: 'Acces non autorise' })}</Alert>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', px: 3, py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <BusinessIcon sx={{ fontSize: 32, color: '#0f766e' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>{org?.name || t('myOrg')}</Typography>
          <Typography fontSize={13} color="text.secondary">
            {t('orgDashboard')} &middot; org #{orgId}
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: t('members'),       value: stats?.total_members   ?? '-', color: '#4f46e5' },
              { label: t('labsActive'),    value: stats?.active_labs     ?? '-', color: '#0f766e' },
              { label: t('creditsUsed'),   value: stats?.credits_used    != null ? stats.credits_used + ' min' : '-', color: '#b45309' },
              { label: t('examPassRate'),  value: stats?.exam_pass_rate  != null ? Math.round(stats.exam_pass_rate) + '%' : '-', color: '#7c3aed',
                sub: t('last30days', { ns: 'common', defaultValue: '30 derniers jours' }) },
            ].map(s => (
              <Grid item xs={6} sm={3} key={s.label}>
                <StatCard {...s} />
              </Grid>
            ))}
          </Grid>

          {/* Quotas */}
          {stats?.credits_quota && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
              <Typography fontSize={13} fontWeight={500} mb={1}>{t('quotas')}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.round((stats.credits_used / stats.credits_quota) * 100))}
                  sx={{ flex: 1, height: 8, borderRadius: 4,
                    '& .MuiLinearProgress-bar': { bgcolor: '#0f766e' } }}
                />
                <Typography fontSize={12} color="text.secondary" noWrap>
                  {stats.credits_used} / {stats.credits_quota} min
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Membres */}
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1,
              borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8fafc' }}>
              <PeopleIcon fontSize="small" color="action" />
              <Typography fontSize={13} fontWeight={500}>{t('members')}</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell>{t('name', { ns: 'common', defaultValue: 'Nom' })}</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>{t('role', { ns: 'common', defaultValue: 'Role' })}</TableCell>
                  <TableCell>{t('creditsUsed')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      {t('noMembers', { ns: 'common', defaultValue: 'Aucun membre' })}
                    </TableCell>
                  </TableRow>
                ) : members.map(m => (
                  <TableRow key={m.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{m.full_name || m.username}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{m.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={t(m.role, { defaultValue: m.role })}
                        size="small"
                        color={m.role === 'teacher' ? 'primary' : m.role === 'org_admin' ? 'secondary' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {m.credits_used != null ? m.credits_used + ' min' : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
}
